import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Onboarding.css';

const slides = [
  {
    id: 1,
    icon: '🏢',
    title: 'نظام إدارة الموارد البشرية الذكي',
    titleEn: 'Smart HR Management System',
    subtitle: 'نظام متكامل لإدارة شؤون الموظفين والحضور والرواتب بأحدث التقنيات',
    subtitleEn: 'Complete system for managing employees, attendance, and payroll with the latest technologies'
  },
  {
    id: 2,
    icon: '👤',
    title: 'سجّل حضورك بالوجه في ثوانٍ',
    titleEn: 'Check-in with Face Recognition in Seconds',
    subtitle: 'تقنية التعرف على الوجه المتقدمة لتسجيل الحضور والانصراف بأمان وسرعة',
    subtitleEn: 'Advanced face recognition technology for secure and fast attendance tracking'
  },
  {
    id: 3,
    icon: '📊',
    title: 'تتبع إجازاتك ومهامك بسهولة',
    titleEn: 'Track Your Leaves and Tasks Easily',
    subtitle: 'إدارة شاملة للإجازات والمهام اليومية مع إشعارات فورية وتقارير مفصلة',
    subtitleEn: 'Comprehensive leave and task management with instant notifications and detailed reports'
  }
];

const Onboarding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const navigate = useNavigate();
  
  const savedLang = localStorage.getItem('helcoo_language');
  const isArabic = savedLang !== 'en';

  // Preload images
  useEffect(() => {
    slides.forEach(slide => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  const goToSlide = (index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => goToSlide(currentSlide + 1);
  const prevSlide = () => goToSlide(currentSlide - 1);

  const goToLogin = () => navigate('/login');
  const goToRegister = () => navigate('/register');

  // Touch handlers for swipe
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) isArabic ? prevSlide() : nextSlide();
    if (distance < -50) isArabic ? nextSlide() : prevSlide();
    setTouchStart(0);
    setTouchEnd(0);
  };

  const slide = slides[currentSlide];

  return (
    <div 
      className="min-h-screen"
      dir={isArabic ? 'rtl' : 'ltr'}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        background: 'linear-gradient(160deg, #FFFFFF 0%, #F8F9FA 50%, #F1F3F5 100%)'
      }}
    >
      {/* Background Circles - Maroon Theme */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute rounded-full animate-pulse-slow"
          style={{
            top: '-20%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle at 30% 30%, rgba(128,0,32,0.12) 0%, rgba(128,0,32,0.06) 30%, rgba(128,0,32,0.02) 50%, transparent 70%)',
          }}
        />
        
        <div 
          className="absolute rounded-full animate-pulse-medium"
          style={{
            top: '15%',
            right: '-15%',
            width: '350px',
            height: '350px',
            background: 'radial-gradient(circle at 40% 40%, rgba(128,0,32,0.1) 0%, rgba(128,0,32,0.05) 40%, transparent 70%)',
          }}
        />
        
        <div 
          className="absolute rounded-full animate-pulse-slow"
          style={{
            top: '30%',
            left: '-20%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle at 60% 40%, rgba(128,0,32,0.08) 0%, rgba(128,0,32,0.04) 40%, transparent 70%)',
          }}
        />
        
        <div 
          className="absolute rounded-full"
          style={{
            bottom: '10%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle at 30% 50%, rgba(128,0,32,0.1) 0%, rgba(128,0,32,0.05) 40%, transparent 70%)',
          }}
        />

        <div 
          className="absolute rounded-full animate-pulse-medium"
          style={{
            top: '45%',
            right: '15%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle at 40% 40%, rgba(189,155,87,0.15) 0%, rgba(189,155,87,0.05) 50%, transparent 70%)',
          }}
        />
      </div>

      {/* Question marks - Hidden for cleaner look */}

      {/* Top bar */}
      <div 
        className="sticky top-0 left-0 right-0 z-20 px-4 flex justify-between items-center"
        style={{
          paddingTop: 'max(env(safe-area-inset-top), 12px)',
          paddingBottom: '8px',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <button 
          onClick={goToLogin}
          className="text-[#1E40AF]/70 hover:text-[#1E40AF] text-sm font-medium transition-colors"
        >
          {isArabic ? 'تخطي' : 'Skip'}
        </button>
        <button
          onClick={() => {
            localStorage.setItem('language', isArabic ? 'en' : 'ar');
            localStorage.setItem('helcoo_language', isArabic ? 'en' : 'ar');
            window.location.reload();
          }}
          className="px-4 py-1.5 text-sm font-medium text-[#1E40AF]/80 border border-[#1E40AF]/20 rounded-full hover:bg-[#1E40AF]/10 transition-all"
        >
          {isArabic ? 'English' : 'العربية'}
        </button>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col min-h-[calc(100vh-70px)] pb-8 px-5">
        {/* Icon Container */}
        <div className="flex-1 flex items-center justify-center relative">
          <div className="relative w-full max-w-xs">
            {/* Glow */}
            <div 
              className="absolute inset-0 rounded-3xl"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(128,0,32,0.15) 0%, transparent 70%)',
                filter: 'blur(40px)',
                transform: 'scale(1.2)'
              }}
            />
            
            {/* Frame with Icon */}
            <div 
              className="relative rounded-[2.5rem] p-8 backdrop-blur-sm overflow-hidden flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(128,0,32,0.08) 0%, rgba(128,0,32,0.02) 100%)',
                border: '1px solid rgba(128,0,32,0.1)',
                boxShadow: '0 25px 50px rgba(128,0,32,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
                minHeight: '280px'
              }}
            >
              {slides.map((s, index) => (
                <div
                  key={s.id}
                  className={`transition-all duration-500 ${
                    index === currentSlide 
                      ? 'opacity-100 scale-100' 
                      : 'opacity-0 scale-75 absolute'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-9xl mb-4 animate-bounce-slow">{s.icon}</div>
                    <div 
                      className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, #1E40AF 0%, #A0153E 100%)',
                        boxShadow: '0 10px 30px rgba(128,0,32,0.3)'
                      }}
                    >
                      <img src="/logo.png" alt="HELCOO" className="w-12 h-12 object-contain" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Text Content */}
        <div className="text-center mt-4 mb-4">
          <h1 
            className="text-2xl md:text-3xl font-black mb-3 leading-tight"
            style={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #A0153E 50%, #1E40AF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {isArabic ? slide.title : slide.titleEn}
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">
            {isArabic ? slide.subtitle : slide.subtitleEn}
          </p>
        </div>

        {/* Slide Indicators */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-8 bg-gradient-to-r from-[#1E40AF] to-[#A0153E]' 
                  : 'w-2 bg-[#1E40AF]/20 hover:bg-[#1E40AF]/40'
              }`}
              style={{
                boxShadow: index === currentSlide ? '0 0 15px rgba(128,0,32,0.3)' : 'none'
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <button
            onClick={currentSlide === slides.length - 1 ? goToLogin : nextSlide}
            className="w-full py-4 rounded-2xl font-bold text-lg transition-transform duration-200 active:scale-[0.98] text-white"
            style={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #A0153E 50%, #1E40AF 100%)',
              boxShadow: '0 10px 40px rgba(128,0,32,0.3), inset 0 2px 0 rgba(255,255,255,0.1)'
            }}
          >
            {currentSlide === slides.length - 1 
              ? (isArabic ? 'تسجيل الدخول' : 'Login')
              : (isArabic ? 'استمر الآن' : 'Continue')
            }
          </button>

          <div className="text-center">
            <span className="text-gray-400">{isArabic ? 'ليس لديك حساب؟' : "Don't have an account?"}</span>
            {' '}
            <button onClick={goToRegister} className="text-[#1E40AF] font-semibold hover:text-[#A0153E] transition-colors">
              {isArabic ? 'سجل الآن' : 'Register Now'}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.8; transform: translateX(-50%) scale(1.05); }
        }
        @keyframes pulse-medium {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.03); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-pulse-slow { animation: pulse-slow 6s ease-in-out infinite; }
        .animate-pulse-medium { animation: pulse-medium 4s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Onboarding;
